/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Random } from '@zedoc/random';
import {
  EyeIcon,
  MenuIcon,
  PencilAltIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Dialog from '../../../../components/Dialog';
import FormFieldText from '../../../../components/forms/FormFieldText';
import FormFieldSearch from '../../../../components/forms/FormFieldSearch';
import FormFieldCheckbox from '../../../../components/forms/FormFieldCheckbox';
import Button from '../../../../components/Button';
import ActionCard from '../ActionCard';
import Tooltip from '../../../../common/components/Tooltip';
import {
  REPORT_TYPE__RESPONSES_DOWNLOAD,
  REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD,
  REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD,
  REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL,
  PDF_SECTION_TYPES,
} from '../../../../common/constants';

const BASE_SECTION_TYPE_OPTIONS = [
  { value: PDF_SECTION_TYPES.CONTAINER, label: 'Container' },
  { value: PDF_SECTION_TYPES.IMAGE, label: 'Image' },
  { value: PDF_SECTION_TYPES.TEXT, label: 'Text' },
  { value: PDF_SECTION_TYPES.CHART, label: 'Chart' },
];

const getSectionTypeOptions = (reportType) => {
  const options = [...BASE_SECTION_TYPE_OPTIONS];

  if (reportType === REPORT_TYPE__RESPONSES_DOWNLOAD) {
    options.push({
      value: PDF_SECTION_TYPES.PATIENT_INFO,
      label: 'Patient Info',
    });
    options.push({
      value: PDF_SECTION_TYPES.PATIENT_RESPONSES,
      label: 'Patient Responses',
    });
  } else if (reportType === REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD) {
    options.push({
      value: PDF_SECTION_TYPES.PATIENT_INFO,
      label: 'Patient Info',
    });

    options.push({
      value: PDF_SECTION_TYPES.ALERT_SINGLE_DETAILS,
      label: 'Alert Details',
    });
    options.push({
      value: PDF_SECTION_TYPES.ALERT_SINGLE_ASSIGNMENT_HISTORY,
      label: 'Alert Assignment History',
    });
    options.push({
      value: PDF_SECTION_TYPES.ALERT_SINGLE_NOTES,
      label: 'Alert Notes',
    });
    // No additional options for alert-summary-single-download
  } else if (reportType === REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD) {
    options.push({
      value: PDF_SECTION_TYPES.PATIENT_INFO,
      label: 'Patient Info',
    });
    options.push({
      value: PDF_SECTION_TYPES.ALERT_SUMMARY_PATIENT,
      label: 'Alert Summary - Patient',
    });
    // No additional options for alert-summary-patient-download
  } else if (reportType === REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL) {
    options.push({
      value: PDF_SECTION_TYPES.ALERTS_DIGEST,
      label: 'Alert Digest',
    });
  } else {
  }
  return options;
};

const getReferenceFieldOptions = (reportType) => {
  if (reportType === REPORT_TYPE__RESPONSES_DOWNLOAD) {
    return [
      { value: 'patientid', label: 'patientid' },
      { value: 'activityid', label: 'activityid' },
      { value: 'milestoneid', label: 'milestoneid' },
      { value: 'projectid', label: 'projectid' },
    ];
  }
  if (reportType === REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD) {
    return [
      { value: 'eappid', label: 'eappid' },
      { value: 'patientid', label: 'patientid' },
      { value: 'projectid', label: 'projectid' },
    ];
  }
  if (reportType === REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD) {
    return [
      { value: 'patientid', label: 'patientid' },
      { value: 'projectid', label: 'projectid' },
    ];
  }
  if (reportType === REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL) {
    return [
      { value: 'targetemail', label: 'targetemail' },
      { value: 'projectid', label: 'projectid' },
    ];
  }
  return [];
};

const ALL_CSS_PROP_OPTIONS = [
  {
    value: 'height',
    label: 'height',
    note: 'Sets the element height. Default: auto. Examples: "100px", "50%", "auto", "fit-content"',
  },
  {
    value: 'width',
    label: 'width',
    note: 'Sets the element width. Default: auto. Examples: "200px", "80%", "100vw", "fit-content"',
  },
  {
    value: 'border',
    label: 'border',
    note: 'Sets border style. Default: none. Examples: "1px solid black", "2px dashed red", "0"',
  },
  {
    value: 'border-width',
    label: 'border-width',
    note: 'Sets the width of the border. Accepts one to four values (top, right, bottom, left). Default: medium. Examples: "1px", "2px 4px", "1px 2px 3px", "1px 2px 3px 4px".',
  },
  {
    value: 'padding',
    label: 'padding',
    note: 'Sets inner spacing. Default: 0. Examples: "10px", "10px 20px", "1em", "5% 10%"',
  },
  {
    value: 'margin',
    label: 'margin',
    note: 'Sets outer spacing. Default: 0. Examples: "10px", "auto", "0 20px", "5% 10%"',
  },
  {
    value: 'color',
    label: 'color',
    note: 'Sets text color. Default: black. Examples: "#f00", "red", "rgba(0,0,0,0.1)", "transparent"',
  },
  {
    value: 'background-color',
    label: 'background-color',
    note: 'Sets background color. Default: transparent. Examples: "#f00", "red", "rgba(0,0,0,0.1)", "transparent"',
  },
  {
    value: 'font-style',
    label: 'font-style',
    note: 'Sets font style. Default: normal. Examples: "normal", "italic", "oblique"',
  },
  {
    value: 'font-family',
    label: 'font-family',
    note: 'Sets font type. Default: depends on browser. Examples: "Arial", "Helvetica, sans-serif", "Times New Roman"',
  },
  {
    value: 'font-size',
    label: 'font-size',
    note: 'Sets font size. Default: medium. Examples: "16px", "1em", "120%", "small", "larger"',
  },
  {
    value: 'text-align',
    label: 'text-align',
    note: 'Aligns inline content. Default: left. Examples: "left", "right", "center", "justify"',
  },
  {
    value: 'font-weight',
    label: 'font-weight',
    note: 'text should be displayed. Examples: "bold", "normal", etc',
  },
];

// Per-type allow-lists (extend / tweak as you like)
const CSS_PROP_BY_TYPE = {
  container: [
    'height',
    'width',
    'padding',
    'border',
    'border-width',
    'margin',
    'color',
    'background-color',
  ],
  image: [
    'height',
    'width',
    'padding',
    'border',
    'border-width',
    'margin',
    'color',
    'background-color',
  ],
  text: [
    'height',
    'width',
    'padding',
    'margin',
    'border',
    'border-width',
    'color',
    'background-color',
    'font-style',
    'font-family',
    'font-size',
    'text-align',
    'font-weight',
  ],
  chart: [
    'height',
    'width',
    'padding',
    'margin',
    'border',
    'border-width',
    // 'color',
    'background-color',
    'font-family',
  ],
  commonInfo: [
    'height',
    'width',
    'padding',
    'margin',
    'border',
    'border-width',
    // 'font-style',
    // 'font-family',
    // 'font-size',
    // 'text-align',
  ],
  responses: [
    'height',
    'width',
    'padding',
    'border',
    'border-width',
    'margin',
    'font-family',
  ],
};

export const getCssPropOptions = (type) => {
  const allowed = CSS_PROP_BY_TYPE[type] || null;
  return allowed
    ? ALL_CSS_PROP_OPTIONS.filter((o) => allowed.includes(o.value))
    : ALL_CSS_PROP_OPTIONS;
};

const PdfSectionListContainer = ({ level, children, onAdd, activeId }) => {
  if (level === 0) {
    return (
      <ActionCard title="Sections" onAdd={onAdd}>
        {children}
      </ActionCard>
    );
  }

  return (
    <div
      className={`mt-2 py-2 px-4 ${
        activeId
          ? 'border border-dashed rounded-md border-divider'
          : 'border-l border-divider'
      }`}
    >
      {children}
    </div>
  );
};

const SortableItem = ({
  id,
  idx,
  currentPath,
  childCount,
  open,
  setExpandedId,
  sections,
  onChange,
  level,
  s,
  remove,
  startAddAt,
  startEdit,
  isPreview,
  reportType,
  readOnly = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li ref={setNodeRef} style={style}>
      <button
        type="button"
        className={`group flex justify-between cursor-pointer rounded-md p-2 transition-colors w-full ${
          isPreview ? 'bg-neutral-400' : 'hover:bg-neutral-400'
        }`}
        onClick={() => setExpandedId(open ? null : s.id)}
        style={{ opacity: !isPreview && isDragging ? 0 : 1 }}
      >
        <div className="cluster-2 items-center min-w-0">
          {!readOnly && (
            <button
              type="button"
              className="cursor-move"
              {...listeners}
              {...attributes}
            >
              <MenuIcon className="w-4 h-4" />
            </button>
          )}
          <p className="font-medium truncate">
            {`${currentPath || ''} [${s.type || '-'}] ${s.name || ''}${
              childCount ? ` [${childCount}]` : ''
            }`}
          </p>
        </div>
        {!isPreview && !readOnly && (
          <div className="flex space-x-1 items-center transition-opacity opacity-0 group-hover:opacity-100">
            <Tooltip title="Remove">
              <Button
                size="medium"
                type="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(idx);
                }}
                icon={<TrashIcon className="text-danger-500" />}
              />
            </Tooltip>
            {s.type === PDF_SECTION_TYPES.CONTAINER && (
              <Tooltip title="Add Sub-Section">
                <Button
                  size="medium"
                  type="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    startAddAt(idx);
                  }}
                  icon={<PlusCircleIcon />}
                />
              </Tooltip>
            )}
            <div className="w-px bg-divider h-4" />
            <Tooltip title="Edit">
              <Button
                size="medium"
                type="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit(idx);
                }}
                icon={<PencilAltIcon />}
              />
            </Tooltip>
          </div>
        )}
        {!isPreview && readOnly && (
          <div className="flex space-x-1 items-center transition-opacity opacity-0 group-hover:opacity-100">
            <Tooltip title="View">
              <Button
                size="medium"
                type="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit(idx);
                }}
                icon={<EyeIcon />}
              />
            </Tooltip>
          </div>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <PdfSectionList
            sections={s.sections || []}
            onChange={(next) => {
              const nextSections = [...sections];
              nextSections[idx] = { ...s, sections: next };
              onChange(nextSections);
            }}
            level={level + 1}
            path={currentPath}
            reportType={reportType}
            readOnly={readOnly}
          />
        </div>
      )}
    </li>
  );
};

const PdfSectionList = ({
  sections = [],
  onChange,
  level = 0,
  path = '',
  reportType = null,
  readOnly = false,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [modal, setModal] = useState(null); // { parentIdx, index, draft }
  const [showPropHint, setShowPropHint] = useState(false);

  /* ------------------------------------------------ helpers */
  const startAdd = () => startAddAt(null);

  const startAddAt = (parentIdx) => {
    setModal({
      parentIdx,
      index: null,
      draft: {
        id: Random.id(),
        name: '',
        type: '',
        value: '',
        title: '',
        description: '',
        name_hide: false,
        direction: '',
        css_properties: [],
        sections: [],
        level: 0,
        referenceFields: [],
      },
    });
  };

  const startEdit = (idx) =>
    setModal({
      parentIdx: null,
      index: idx,
      draft: {
        ...sections[idx],
        css_properties: sections[idx].css_properties || [],
        referenceFields: sections[idx].referenceFields || [],
        title: sections[idx].title || '',
        description: sections[idx].description || '',
      },
    });

  const remove = (idx) => onChange(sections.filter((_, i) => i !== idx));

  const handleModalChange = (field, value) =>
    setModal((m) => ({ ...m, draft: { ...m.draft, [field]: value } }));

  const addCssProp = () =>
    setModal((m) => ({
      ...m,
      draft: {
        ...m.draft,
        css_properties: [
          ...(m.draft.css_properties || []),
          { key: '', value: '' },
        ],
      },
    }));

  const updateCssProp = (idx, field, val) =>
    setModal((m) => {
      const next = [...(m.draft.css_properties || [])];
      next[idx] = { ...next[idx], [field]: val };
      return { ...m, draft: { ...m.draft, css_properties: next } };
    });

  const removeCssProp = (idx) =>
    setModal((m) => ({
      ...m,
      draft: {
        ...m.draft,
        css_properties: (m.draft.css_properties || []).filter(
          (_, i) => i !== idx,
        ),
      },
    }));

  const saveModal = () => {
    const { parentIdx, index, draft } = modal;

    if (!draft.name.trim() || !draft.type.trim()) {
      alert('Both "Name" and "Type" are required.');
      return;
    }
    if (
      draft.type === PDF_SECTION_TYPES.CHART &&
      !String(draft.value || '').trim()
    ) {
      alert('Chart ID is required.');
      return;
    }

    // add level in to section
    draft.level = level;

    if (parentIdx === null) {
      const next = [...sections];
      if (index === null) next.push(draft);
      else next[index] = draft;
      onChange(next);
      setExpandedId(next.id);
    } else {
      const next = [...sections];
      const parent = next[parentIdx];
      const childList = [...(parent.sections || [])];
      if (index === null) childList.push(draft);
      else childList[index] = draft;
      next[parentIdx] = { ...parent, sections: childList };
      onChange(next);
      setExpandedId(parent.id);
    }

    setModal(null);
  };

  // Dnd currently dragged item
  const [activeId, setActiveId] = useState(null);
  const activeItem = sections.find((s) => s.id === activeId);

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const handleDragEnd = (event) => {
    setActiveId(null);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(sections, oldIndex, newIndex);
      onChange(reordered);
    }
  };

  /* ------------------------------------------------ render */
  return (
    <PdfSectionListContainer
      level={level}
      onAdd={readOnly ? undefined : startAdd}
      activeId={activeId}
    >
      {sections.length ? (
        <ul className="space-y-2 list-none p-0 m-0">
          <DndContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((s, idx) => {
                const currentPath = path ? `${path}.${idx + 1}` : `${idx + 1}`;
                const childCount = (s.sections || []).length;
                const open = expandedId === s.id;

                return (
                  <SortableItem
                    key={s.id}
                    id={s.id}
                    idx={idx}
                    currentPath={currentPath}
                    childCount={childCount}
                    open={open}
                    setExpandedId={setExpandedId}
                    sections={sections}
                    onChange={onChange}
                    level={level}
                    s={s}
                    remove={remove}
                    startAddAt={startAddAt}
                    startEdit={startEdit}
                    reportType={reportType}
                    readOnly={readOnly}
                  />
                );
              })}
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeItem ? <SortableItem s={activeItem} isPreview /> : null}
            </DragOverlay>
          </DndContext>
        </ul>
      ) : (
        <p className="text-xs">No sections yet.</p>
      )}

      <Dialog
        visible={Boolean(modal)}
        size="medium"
        title={
          modal
            ? readOnly
              ? 'View Section'
              : modal.index === null
              ? 'Add Section'
              : 'Edit Section'
            : ''
        }
        onCancel={() => setModal(null)}
        onOk={readOnly ? undefined : saveModal}
        footer={readOnly ? null : undefined}
      >
        {modal && (
          <div className="space-y-4">
            <FormFieldText
              name="sectionName"
              label="Name"
              input={{
                value: modal.draft.name || '',
                onChange: (val) => handleModalChange('name', val),
              }}
              meta={{ submitFailed: false, error: null, formulaError: null }}
              required
              placeholder="Section name"
              disabled={readOnly}
              type="text"
            />

            <FormFieldSearch
              label="Type"
              input={{
                value: modal.draft.type,
                onChange: (val) => handleModalChange('type', val),
              }}
              meta={{ submitFailed: false, error: null }}
              required
              placeholder="Select type"
              options={getSectionTypeOptions(reportType)}
              mode="single"
              allowClear
              disabled={readOnly}
            />

            {modal.draft.type &&
              modal.draft.type !== PDF_SECTION_TYPES.TEXT && (
                <div>
                  <FormFieldText
                    name="sectionTitle"
                    label="Title"
                    input={{
                      value: modal.draft.title || '',
                      onChange: (val) => handleModalChange('title', val),
                    }}
                    meta={{
                      submitFailed: false,
                      error: null,
                      formulaError: null,
                    }}
                    required={false}
                    placeholder="Section title (optional)"
                    disabled={readOnly}
                    type="text"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Note: Keep empty if you don't want to show it in the report.
                  </p>
                  <FormFieldText
                    name="sectionDescription"
                    label="Description"
                    input={{
                      value: modal.draft.description || '',
                      onChange: (val) => handleModalChange('description', val),
                    }}
                    meta={{
                      submitFailed: false,
                      error: null,
                      formulaError: null,
                    }}
                    required={false}
                    placeholder="Section description (optional)"
                    disabled={readOnly}
                    type="freeText"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Note: Keep empty if you don't want to show it in the report.
                  </p>
                </div>
              )}

            {modal.draft.type === PDF_SECTION_TYPES.CHART &&
              getReferenceFieldOptions(reportType).length > 0 && (
                <div>
                  <FormFieldSearch
                    label="Reference Fields"
                    input={{
                      value: modal.draft.referenceFields || [],
                      onChange: (val) =>
                        handleModalChange('referenceFields', val),
                    }}
                    meta={{ submitFailed: false, error: null }}
                    options={getReferenceFieldOptions(reportType)}
                    mode="multiple"
                    allowClear
                    placeholder="Select reference fields"
                    disabled={readOnly}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Note: These fields are used to filter chart data from BI.
                    The field names must exist in the chart dataset with the
                    same name.
                  </p>
                </div>
              )}

            {[
              PDF_SECTION_TYPES.IMAGE,
              PDF_SECTION_TYPES.TEXT,
              PDF_SECTION_TYPES.CHART,
            ].includes(modal.draft.type) && (
              <div>
                <FormFieldText
                  name="sectionValue"
                  label="Value"
                  input={{
                    value: modal.draft.value || '',
                    onChange: (val) => handleModalChange('value', val),
                  }}
                  meta={{
                    submitFailed: false,
                    error: null,
                    formulaError: null,
                  }}
                  required={false}
                  placeholder={
                    modal.draft.type === PDF_SECTION_TYPES.CHART
                      ? 'Chart ID'
                      : 'Optional value'
                  }
                  disabled={readOnly}
                  type="freeText"
                />
                {modal.draft.type === PDF_SECTION_TYPES.CHART && (
                  <p className="text-xs text-gray-500 mt-1">
                    Note: Value takes the chart ID from BI.
                  </p>
                )}
              </div>
            )}

            {modal.draft.type === PDF_SECTION_TYPES.CHART && (
              <FormFieldCheckbox
                name="nameHide"
                label="Hide name"
                input={{
                  value: modal.draft.name_hide || false,
                  onChange: (e) =>
                    handleModalChange('name_hide', e.target.checked),
                }}
                meta={{ submitFailed: false, error: null, formulaError: null }}
                placeholder="Optional value"
                disabled={readOnly}
              />
            )}

            {modal.draft.type === PDF_SECTION_TYPES.CONTAINER && (
              <FormFieldSearch
                label="Child Direction"
                input={{
                  value: modal.draft.direction || '',
                  onChange: (val) => handleModalChange('direction', val),
                }}
                meta={{ submitFailed: false, error: null }}
                options={[
                  { value: 'row', label: 'Row' },
                  { value: 'column', label: 'Column' },
                ]}
                mode="single"
                allowClear
                placeholder="Select direction"
                disabled={readOnly}
              />
            )}

            {/* CSS props */}
            <ActionCard
              title="CSS Properties"
              onAdd={readOnly ? undefined : addCssProp}
              onShowHint={() => setShowPropHint((v) => !v)}
            >
              {modal.draft.css_properties?.length === 0 && (
                <p className="text-xs text-gray-400 mb-2">None yet.</p>
              )}
              {modal.draft.css_properties?.map((p, pIdx) => (
                <div key={pIdx} className="flex space-x-2 items-end mb-2">
                  <div className="flex-1">
                    <FormFieldSearch
                      label="CSS Key"
                      input={{
                        value: p.key || '',
                        onChange: (val) => updateCssProp(pIdx, 'key', val),
                      }}
                      meta={{ submitFailed: false, error: null }}
                      options={getCssPropOptions(modal.draft.type)}
                      mode="single"
                      allowClear
                      placeholder="CSS property"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="flex-1">
                    <FormFieldText
                      name={`css-value-${pIdx}`}
                      label="Value"
                      input={{
                        value: p.value || '',
                        onChange: (val) => updateCssProp(pIdx, 'value', val),
                      }}
                      meta={{ submitFailed: false, error: null }}
                      placeholder="value"
                      type="text"
                      disabled={readOnly}
                    />
                  </div>
                  {!readOnly && (
                    <Button
                      type="tertiary"
                      onClick={() => removeCssProp(pIdx)}
                      icon={<TrashIcon className="text-danger-500" />}
                    />
                  )}
                </div>
              ))}
              <Dialog
                title="Hint"
                visible={showPropHint}
                onOk={() => setShowPropHint((v) => !v)}
                onCancel={() => setShowPropHint((v) => !v)}
              >
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left font-semibold pb-1">Key</th>
                      <th className="text-left font-semibold pb-1">
                        Note / default
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCssPropOptions(modal.draft.type).map((opt) => (
                      <tr key={opt.value} className="border-t">
                        <td className="py-1 pr-2 whitespace-nowrap">
                          {opt.label}
                        </td>
                        <td className="py-1">{opt.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Dialog>
            </ActionCard>
          </div>
        )}
      </Dialog>
    </PdfSectionListContainer>
  );
};

export default PdfSectionList;
